import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DiscoverRoutingService } from '../../../../Discover/DiscoverRouter/services/discover-routing.service';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { OutletModel } from '../../../../Discover/Targeting/models/outlet.model';
import { ContactModel } from '../../../TargetingShared/model/contact.model';
import { BaseJournodexEntityModel } from '../../../../Discover/Targeting/models/base-journodex-entity.model';
import { ContactService } from '../../../TargetingShared/services/contact-service';
import { EmploymentModel } from '../../../../Discover/Targeting/models/employment.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { MentionSearchCriteriaModel } from '../../../../Monitor/Shared/models/mention-search-criteria.model';
import { JournodexSourceModel } from '../../../Journodex/models/journodex-source.model';
import { SearchCriteriaSourcesEnum } from '../../../../api/mentions/enum/search-criteria-sources.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { PrivateDataModel } from '../../../../Discover/Targeting/models/private-data.model';
import { TargetingRhpContextsService } from '../../../TargetingShared/services/targeting-rhp-contexts.service';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { TargetingSearchService } from '@Discover/Targeting/services/targeting-search.service';

@Component({
    selector: 'agpr-entity-full-profile',
    templateUrl: './entity-full-profile.component.html',
    styleUrls: ['./entity-full-profile.component.scss'],
    standalone: false
})
export class EntityFullProfileComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() id: string;
    @Input() set entity (entity: BaseJournodexEntityModel) {
        if (!entity) {
            return;
        }
        this.dialogMode = true;
        this.entityType = entity.entityType;
        if (this.entityType === EntityType.OUTLET) {
            this.targetingRhpContextsService.setFullProfileOutlet(<OutletModel> entity);
            this.title = 'Discover.entityProfile.outletDetails';
        } else {
            this.targetingRhpContextsService.setFullProfileContact(<ContactModel> entity, (<ContactModel> entity)?.selectedEmployment?.id);
            this.title = 'Discover.entityProfile.contactDetails';
        }
    }
    @Input() set contactPrivateData(privateData: PrivateDataModel) {
        this._contactPrivateData = privateData;
        this.targetingRhpContextsService.setContactPrivateData(privateData);
    }
    @Input() set outletPrivateData(privateData: PrivateDataModel) {
        this._outletPrivateData = privateData;
        this.targetingRhpContextsService.setOutletPrivateData(privateData);
    }

    @Output() exit: EventEmitter<number> = new EventEmitter();
    @Output() showContactEditor: EventEmitter<void> = new EventEmitter();

    @Input() backButtonLocalization: string;

    public entityId: string;
    public entityType: EntityType;

    _entity: BaseJournodexEntityModel;
    contactModel: ContactModel;
    outletModel: OutletModel;
    _contactPrivateData: PrivateDataModel;
    _outletPrivateData: PrivateDataModel;

    editingContact: boolean;
    currentUser: UserModel;
    scrollTop: number;

    selectedEmployment: EmploymentModel;
    newSelectedEmploymentId: number;

    subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly entityTypes = EntityType;

    userHasMonitoring = false;
    dialogMode = false;
    panelHeight: string;
    title: string = '';

    navHeightObserver = new ResizeObserver((entry) => {
        const navHeight = entry[0].contentRect.height;
        this.panelHeight = (window.innerHeight - navHeight) + 'px';
        this.cd.detectChanges();
    });

    @ViewChild('navBackButton') navBackButton: ElementRef;

    constructor(
        public router: Router,
        public discoverRoutingService: DiscoverRoutingService,
        public currentUserService: CurrentUserStateService,
        public contactService: ContactService,
        public coreDialogService: CoreDialogService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public cd: ChangeDetectorRef,
        public titleService: Title,
        public localizationService: LocalizationService,
        public targetingSearchService: TargetingSearchService,
    ) {}

    ngOnInit() {
        this.subscriptions.push(
            this.currentUserService.getPermissions().subscribe(permissions => {
                this.userHasMonitoring = !!permissions.find(x => x === userPermissionsEnum.advancedMonitoring);
            }),
            this.discoverRoutingService.getCurrentRoute().subscribe(route => {
                // TODO : I think this is a race condition between the input
                // and the router sub, but it seems to work.
                if (!this.dialogMode) {
                    this.entityId = route.value;
                    this.entityType = EntityType[route.queryParamMap?.['entityType']];
                    this.setEntityFromId();
                }
            }),
            this.targetingRhpContextsService.getContact().subscribe(contact => {
                if (this.entityType === EntityType.CONTACT && contact) {
                    this.contactModel = contact;
                    this._entity = contact;
                    this.selectedEmployment = contact.selectedEmployment;
                    if (!this.dialogMode) {
                        this.titleService.setTitle(
                            this.localizationService.transform(
                                'Discover.contact.browserTitle', {first: this.contactModel.firstName, last: this.contactModel.lastName}
                            )
                        );
                    }
                }
            }),
            this.targetingRhpContextsService.getOutlet().subscribe(outlet => {
                if (this.entityType === EntityType.OUTLET && outlet && outlet.outlet) {
                    this.outletModel = outlet.outlet;
                    this._entity = outlet.outlet;

                    if (!this.dialogMode) {
                        this.titleService.setTitle(
                            this.localizationService.transform('Discover.outlet.browserTitle', {name: this.outletModel.name})
                        );
                    }
                }
            }),
            this.targetingRhpContextsService.getContactPrivateData().pipe(filter(x => !!x)).subscribe(privateData => {
                this._contactPrivateData = privateData;
            }),
            this.targetingRhpContextsService.getOutletPrivateData().pipe(filter(x => !!x)).subscribe(privateData => {
                this._outletPrivateData = privateData;
            }),
            this.contactService.getPrivateDataAllLayerUpdate().pipe(filter(x => !!x)).subscribe(update => {
                if (update.entityId === this._entity.id) {
                    if (update.entityType === EntityType.CONTACT) {
                        this._contactPrivateData = update.privateData;
                    } else {
                        this._outletPrivateData = update.privateData;
                    }
                }
            }),
            this.currentUserService.getUser().subscribe(x => this.currentUser = x)
        );
        const nav = document.querySelector('.agpr-masterLayout-navigation');
        this.panelHeight = (window.innerHeight - nav.clientHeight) + 'px';
        this.navHeightObserver.observe(nav);
    }

    ngAfterViewInit(): void {
        // HACK: when opening full profile from take over, the take over would be closed, and dialog's trap focus would return the focus
        // to the trigger that opened the take over, so the button was not getting focused
        // Waiting until the take over is closed and focus transferred to move the focus back to the current full profile
        setTimeout(() => {
            this.navBackButton?.nativeElement.focus();
        }, 600);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    setEntityFromId() {
        if (!this.entityId || !this.entityType) {

            return;
        }
        if (this.entityType === EntityType.CONTACT) {
            this.targetingRhpContextsService.setFullProfileContactById(this.entityId);
        } else {
            this.targetingRhpContextsService.setFullProfileOutletById(this.entityId);
        }
        // TODO : handle 404
        /*
        (err) => {
            if (err.status === 404) {
                this.coreDialogService.open(ConfirmDialogComponent, {
                    inputs: {
                        heading: `Discover.${this.entityType.toLowerCase()}.notFound.header`,
                        message: `Discover.${this.entityType.toLowerCase()}.notFound.message`
                    }
                });
            }
        });*/
    }

    handleSelectedEmploymentChanged(employment: EmploymentModel) {
        if (this.entityType === EntityType.CONTACT) {
            this.contactModel.selectedEmployment = employment;
            this.selectedEmployment = this.contactModel.selectedEmployment;
            this.targetingRhpContextsService.changeSelectedEmployment(employment.id);
            this.newSelectedEmploymentId = employment.id;
        }
    }

    scrollTopChanged(scrollTop: number) {
        this.scrollTop = scrollTop;
    }

    navigateBack(event?: KeyboardEvent) {
        if (event && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        this.exit.emit(this.newSelectedEmploymentId);
    }

    navigateToMonitoring() {
        const context = new MentionSearchCriteriaModel();
        const sourceModel = new JournodexSourceModel();
        sourceModel.id = this.entityId;
        sourceModel.entityType = this.entityType === EntityType.CONTACT ?
            SearchCriteriaSourcesEnum.journalist :
            SearchCriteriaSourcesEnum.outlet;
        sourceModel.entityName = this.entityType === EntityType.CONTACT ?
            this.contactModel.firstName + ' ' + this.contactModel.lastName :
            this.outletModel.name;

        context.query.includedSources = [sourceModel];

        const url = this.router.serializeUrl(
            this.router.createUrlTree(['Monitoring', 'search', 'results'], {
                queryParams: {
                    filters: MentionSearchCriteriaModel.toFilterQueryString(context)
                }
            })
        );
        window.open(url, '_blank');
    }
}
